import { skipToken } from '@reduxjs/toolkit/query';
import { formatDate } from '@strata/helper-functions';
import { DollarAmount } from '@strata/react-dollar-amount';
import { FormSectionWithRows } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { type ColumnDef, Grid, PrimaryData, SecondaryData } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { ClaimStatusIndicator } from '@strata/react-status-indicators';
import { type ComponentProps, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { ClaimEstimateDialog } from 'components/wonReferrals/claimSubmission/dialogs/ClaimEstimateDialog';
import { AppRoutes } from 'services/routes';
import {
    type ClaimForReferral,
    useGetClaimDetailsQuery,
    useGetClaimsForReferralQuery,
} from 'store/api/medbillingApi';
import { type ReferralDetails } from 'store/api/treatmentApi';

const initialSortProperties = [{ id: 'processedDate', desc: true }];

const claimDollarAmountCol = (header: string) => ({
    header,
    headerStyle: { justifyContent: 'flex-end' },
    size: 100,
    cell: ({ getValue }: { getValue: () => number | undefined }) => (
        <div className="text-align--right">
            <PrimaryData data={<DollarAmount amount={getValue()} showZeroValue />} />
        </div>
    ),
});

const columns = (isClaimsGrid: boolean): ColumnDef<ClaimForReferral>[] => [
    {
        accessorKey: 'processedDate',
        header: 'Processed Date',
        size: 100,
        cell: ({ getValue }) => <SecondaryData data={formatDate(getValue())} />,
    },
    {
        ...claimDollarAmountCol('Submitted Amt.'),
        accessorKey: 'submittedAmount',
    },
    {
        ...claimDollarAmountCol('Contractual Obligation'),
        accessorKey: 'contractualObligation',
    },
    {
        ...claimDollarAmountCol('Payer Amt.'),
        accessorKey: 'payerAmount',
    },
    {
        ...claimDollarAmountCol('Other Adj.'),
        accessorKey: 'otherAdj',
    },
    {
        ...claimDollarAmountCol('Member Liability'),
        accessorKey: 'memberLiability',
    },
    {
        ...claimDollarAmountCol('Adj. Total'),
        accessorKey: 'adjTotal',
    },
    {
        ...claimDollarAmountCol(isClaimsGrid ? 'Paid Amt.' : 'Est. Payment Amt.'),
        accessorKey: 'claimTotal',
    },
    {
        accessorKey: 'claimStatus',
        header: isClaimsGrid ? 'Claim Status' : '',
        size: 120,
        cell: ({ getValue }) =>
            isClaimsGrid ? <ClaimStatusIndicator status={getValue()} /> : null,
    },
];

interface ClaimsAndEstimatesGridProps {
    title: string;
    payerId?: string;
    data?: ClaimForReferral[];
    onRowClick: ComponentProps<typeof Grid<ClaimForReferral>>['onRowClick'];
    isClaimsGrid?: boolean;
}

const ClaimsAndEstimatesGrid = ({
    title,
    data = undefined,
    payerId = undefined,
    onRowClick,
    isClaimsGrid = false,
}: ClaimsAndEstimatesGridProps) => (
    <div className="sds_claims-and-estimates">
        <div className="sds_claims-and-estimates__title padding__bottom--medium">{title}</div>
        {payerId === 'HM001' && (data ?? []).length > 0 && (
            <div className="padding__bottom--medium">
                <InlineMessage
                    intent={InlineMessage.Intent.INFO}
                    label="These claims were submitted in batch. Real-time adjustments are not available."
                />
            </div>
        )}
        <Grid
            data={data}
            onRowClick={onRowClick}
            sortProperties={initialSortProperties}
            noResults={
                <div className="sds_claims-and-estimates__no-results padding__bottom--medium">
                    <span className="sds_claims-and-estimates__title">{`No ${title} Found`}</span>
                </div>
            }
            columns={columns(isClaimsGrid)}
        />
    </div>
);

interface ClaimsAndEstimatesProps {
    referralId: string;
    payerId?: string;
    referralDetails?: ReferralDetails;
}

export const ClaimsAndEstimates = ({
    referralId,
    payerId = undefined,
    referralDetails = undefined,
}: ClaimsAndEstimatesProps) => {
    const navigate = useNavigate();
    const location = useLocation();

    const { data: claims } = useGetClaimsForReferralQuery(
        referralId && payerId ? { referralId, payerId } : skipToken,
    );

    const [selectedEstimateId, setSelectedEstimateId] = useState<string>();
    const { data } = useGetClaimDetailsQuery(
        selectedEstimateId && payerId ? { transactionId: selectedEstimateId, payerId } : skipToken,
    );
    const estimateDetails = selectedEstimateId ? data : undefined;

    return (
        <>
            <FullscreenLoading
                isShowing={!!selectedEstimateId && !estimateDetails}
                loadingText="Loading estimate information..."
            />
            <ClaimEstimateDialog
                referralId={referralId}
                referralDetails={referralDetails}
                estimateData={estimateDetails}
                isOpen={!!estimateDetails}
                onClose={() => setSelectedEstimateId(undefined)}
            />
            <FormSectionWithRows
                sectionTitle="Claims And Estimates"
                isLoading={!claims}
                rowOneContent={
                    <ClaimsAndEstimatesGrid
                        title="Claims"
                        data={claims?.filter((claim) => !claim.isEstimate)}
                        payerId={payerId}
                        isClaimsGrid
                        onRowClick={(rowData) =>
                            navigate(AppRoutes.claimDetails(rowData.transactionId), {
                                state: {
                                    from: location,
                                },
                            })
                        }
                    />
                }
                rowTwoContent={
                    <ClaimsAndEstimatesGrid
                        title="Estimates"
                        data={claims?.filter((claim) => claim.isEstimate)}
                        onRowClick={(rowData) => setSelectedEstimateId(rowData.transactionId)}
                    />
                }
            />
        </>
    );
};
